import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const WEBINAR_URL = "https://learn.indiemusiccreators.com/l/734bc4cedb?purchaseNow=true";

// Helper function to get the next Sunday at 1 PM IST
const getNextSunday = () => {
  const now = new Date();
  const nextSunday = new Date(now);
  
  // Get days until next Sunday (0 = Sunday, 1 = Monday, etc.)
  const daysUntilSunday = (7 - now.getDay()) % 7;
  
  // If today is Sunday and it's before 1 PM, use today
  if (now.getDay() === 0 && now.getHours() < 13) {
    nextSunday.setHours(13, 0, 0, 0);
  } else if (daysUntilSunday === 0) {
    // If today is Sunday but after 1 PM, move to next Sunday
    nextSunday.setDate(now.getDate() + 7);
    nextSunday.setHours(13, 0, 0, 0);
  } else {
    // Move to next Sunday
    nextSunday.setDate(now.getDate() + daysUntilSunday);
    nextSunday.setHours(13, 0, 0, 0);
  }
  
  return nextSunday;
};

// Helper function to format date for display
const formatWebinarDate = (date: Date) => {
  const day = date.getDate();
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                 day === 2 || day === 22 ? 'nd' : 
                 day === 3 || day === 23 ? 'rd' : 'th';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day}${suffix} ${months[date.getMonth()]}`;
};

// Countdown Timer Component
const CountdownTimer = () => {
  const { currentTheme } = useTheme();
  const targetDate = getNextSunday();
  const [timeLeft, setTimeLeft] = React.useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [show, setShow] = React.useState(false);
  const [hasScrolled, setHasScrolled] = React.useState(false);
  const lastScrollY = React.useRef(0);

  React.useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);
        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current) {
        setShow(true); // scrolling down
      } else if (currentScrollY < lastScrollY.current) {
        setShow(false); // scrolling up
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`hidden md:flex fixed bottom-0 left-0 w-full z-50 justify-center pointer-events-none transition-transform duration-300 ${show ? 'translate-y-0' : 'translate-y-full'}`}
      style={{ willChange: 'transform' }}
    >
      <div className={`pointer-events-auto bg-gradient-to-r ${currentTheme.colors.primary.replace('from-', 'from-').replace('to-', 'to-')} text-white px-2 sm:px-6 py-3 rounded-t-2xl shadow-2xl flex items-center text-lg md:text-xl font-bold border-t-4 border-white/40 animate-fade-in mx-auto overflow-x-auto whitespace-nowrap min-w-0`}>
        <div className="flex flex-row items-center gap-4 md:gap-6">
          <span className="flex flex-col leading-tight text-left min-w-[120px] md:min-w-[140px]">
            <span className="whitespace-nowrap">Free Webinar</span>
            <span>Starts in:</span>
          </span>
          <span className="flex gap-2 items-center">
            <span className="bg-white/10 px-3 py-1 rounded-lg">{String(timeLeft.days).padStart(2, '0')}<span className="text-xs font-normal ml-1">d</span></span>
            <span>:</span>
            <span className="bg-white/10 px-3 py-1 rounded-lg">{String(timeLeft.hours).padStart(2, '0')}<span className="text-xs font-normal ml-1">h</span></span>
            <span>:</span>
            <span className="bg-white/10 px-3 py-1 rounded-lg">{String(timeLeft.minutes).padStart(2, '0')}<span className="text-xs font-normal ml-1">m</span></span>
            <span>:</span>
            <span className="bg-white/10 px-3 py-1 rounded-lg">{String(timeLeft.seconds).padStart(2, '0')}<span className="text-xs font-normal ml-1">s</span></span>
          </span>
        </div>
        <a
          href={WEBINAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-5 md:py-3 md:px-8 rounded-lg text-base md:text-xl transition-all shadow-xl whitespace-nowrap ml-2 md:ml-4 flex-shrink-0 border-2 border-white/70"
          style={{ pointerEvents: 'auto' }}
        >
          Register now
        </a>
      </div>
    </div>
  );
};  

const Webinar = () => {
  const nextWebinarDate = getNextSunday();
  const formattedDate = formatWebinarDate(nextWebinarDate);
  const { currentTheme } = useTheme();
  
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Free Masterclass for Singers — Become an Original Artist in 90 Days | Mukesh Rathore</title>
        <meta
          name="description"
          content="Join the free live webinar and discover the step-by-step system to create, produce, and release your first original song in 90 days—even if you're stuck or overwhelmed right now."
        />
        <meta
          property="og:title"
          content="Free Webinar: Become an Original Artist in 90 Days | Mukesh Rathore"
        />
        <meta
          property="og:description"
          content="A step-by-step system for everyday people to turn scattered ideas into finished, released original songs."
        />
        <link rel="canonical" href="https://mukeshrathore.com/zoom" />
      </Helmet>

      {/* HERO SECTION */}
      <section className={`pt-4 pb-10 md:pb-20 px-4 md:px-8 bg-gradient-to-br ${currentTheme.colors.primary} text-white relative overflow-hidden`}>
        
        <div className="max-w-7xl mx-auto relative z-10">
          

          {/* 90-Min FREE Workshop - Highlighted */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-center mb-6"
          >
            <motion.a
              href={WEBINAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-purple-900 px-6 py-1 rounded-full font-extrabold text-base md:text-lg shadow-2xl border-2 border-white/50 cursor-pointer transition-all"
            >
              90-Min FREE Workshop
            </motion.a>
          </motion.div>

          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-center mb-4 max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              Singers: Stop Just Practicing <br/><span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Start Becoming an Original Artist</span>
              
            </h1>
          </motion.div>

          {/* Minor Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-center mb-12 max-w-4xl mx-auto"
          >
            <p className="text-base md:text-lg text-white/90 leading-relaxed mb-4">
              This free webinar is for singers who want to move beyond practice and covers — and become original artists with their own songs.
              You’ll learn a simple, home-based system to go from<br/><span className="bg-gradient-to-r from-teal-400 to-yellow-500 bg-clip-text text-transparent">👉 Zero → Melody → Original Song</span>
            </p>
          </motion.div>

          {/* Video and Event Details Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-col lg:flex-row gap-8 items-start lg:items-center max-w-6xl mx-auto"
          >
            {/* Video - Left Side */}
            <div className="flex-1 w-full">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/30" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/mEvSQubAURs?rel=0&modestbranding=1"
                  title="Webinar Preview"
                  frameBorder="0"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            {/* Event Details with CTA - Right Side */}
            <div className="flex-1 w-full lg:max-w-md">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20 shadow-2xl">
                <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">Event Details</h3>
                
                <div className="space-y-4 mb-6">
                  {/* Date and Time */}
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/20 border border-white/30">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-3xl">🗓️</span>
                      <div className="flex flex-col">
                        <span className="text-purple-200 text-sm">Date</span>
                        <span className="text-lg font-semibold">{formattedDate}</span>
                      </div>
                    </div>
                    <div className="w-px h-12 bg-white/30"></div>
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-3xl">⏰</span>
                      <div className="flex flex-col">
                        <span className="text-purple-200 text-sm">Time</span>
                        <span className="text-lg font-semibold">1:00 PM IST</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Language and Duration */}
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/20 border border-white/30">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-3xl">🗣️</span>
                      <div className="flex flex-col">
                        <span className="text-purple-200 text-sm">Language</span>
                        <span className="text-lg font-semibold">English</span>
                      </div>
                    </div>
                    <div className="w-px h-12 bg-white/30"></div>
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-3xl">⏳</span>
                      <div className="flex flex-col">
                        <span className="text-purple-200 text-sm">Duration</span>
                        <span className="text-lg font-semibold">90 Min</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <motion.a
                  href={WEBINAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    x: [0, -8, 8, -8, 8, 0]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 0.8,
                    ease: "easeInOut"
                  }}
                  className="block w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 px-8 rounded-xl text-xl text-center transition-all shadow-2xl border-2 border-white/30"
                >
                  Join The Free Masterclass
                </motion.a>
                
                {/* Limited Seats Notice */}
                <p className="text-center mt-4 text-sm text-yellow-200 font-semibold">
                  ⚡ Limited Seats • Save Your Spot Now
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-10 md:py-20 px-2 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
              🎯 Who This Webinar Is For
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              "You are a singer who wants to create original songs, not just practice or sing covers",
              "You may be self-taught or classically trained — but no one has shown you how singers become original artists",
               "You have ideas but struggle to create a strong melodic idea that becomes a real song",
              "You don’t understand the complete song-creation process from melody to release",
              "You want a clear, step-by-step system — not random tips or shortcuts"
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-l-4 border-purple-500"
              >
                <span className="text-2xl flex-shrink-0">✓</span>
                <span className="text-lg text-gray-700">{item}</span>
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <motion.a
              href={WEBINAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                x: [0, -8, 8, -8, 8, 0]
              }}
              transition={{
                repeat: Infinity,
                duration: 0.8,
                ease: "easeInOut"
              }}
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 md:py-6 md:px-12 rounded-lg text-lg md:text-2xl transition-all shadow-2xl"
            >
              Join The Free Masterclass
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* OUTCOME */}
      <section className={`py-10 md:py-20 px-2 md:px-6 bg-gradient-to-r ${currentTheme.colors.primary} text-white`}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-8">
              ✅ What You'll Learn in This Free Masterclass
            </h2>
          </motion.div>

          

          {/* Outcome Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-yellow-400 mb-6">
              In 90 days, you can go from singer to original artist — and release your first original song
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-10">
              This free masterclass shows you the exact path — without high-end studios, labels, or confusion
            </p>
          </motion.div>

          {/* CTA Button */}
          <div className="text-center">
            <motion.a
              href={WEBINAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                x: [0, -8, 8, -8, 8, 0]
              }}
              transition={{
                repeat: Infinity,
                duration: 0.8,
                ease: "easeInOut"
              }}
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 md:py-6 md:px-12 rounded-lg text-lg md:text-2xl transition-all shadow-2xl mb-4"
            >
              Join The Free Masterclass
            </motion.a>
           
          </div>
        </div>
      </section>

      {/* CREDENTIALS SECTION */}
      <section className="py-10 md:py-20 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left side - Credentials */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Learn From Someone Who's Walked the Path
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <span className="text-3xl flex-shrink-0">🎙️</span>
                  <p className="text-lg text-gray-700">
                    Independent singer-songwriter with <strong>45+ original songs released</strong>
                  </p>
                </div>
                    <div className="flex items-start gap-4">
                  <span className="text-3xl flex-shrink-0">👥</span>
                  <p className="text-lg text-gray-700">
                      Founder of <strong>Indie Music Creators Community</strong> — helping everyday music lovers become independent artists
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-3xl flex-shrink-0">👥</span>
                  <p className="text-lg text-gray-700">
                    Founder of RagaSpectrum Online Music Academy with <strong>15,000+ learners</strong>
                  </p>
                </div>
             
              
                
                <div className="flex items-start gap-4">
                  <span className="text-3xl flex-shrink-0">🌍</span>
                  <p className="text-lg text-gray-700">
                    Songs available across <strong>Spotify, Apple Music, YouTube, and more</strong>
                  </p>
                </div>
                
                <div className="flex items-start gap-4">
                  <span className="text-3xl flex-shrink-0">🎓</span>
                  <p className="text-lg text-gray-700">
                    <strong>28 years</strong> of music journey — Indian classical training + modern music production
                  </p>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t-2 border-gray-200">
                <p className="text-2xl font-bold text-gray-900 italic">
                  I don't teach theory. I teach completion.
                </p>
              </div>
            </motion.div>

            {/* Right side - Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col items-center gap-4"
            >
              <img
                src="/images/Both thumbsup - headphone neck T.png"
                alt="Mukesh Rathore"
                loading="lazy"
                width="448"
                height="448"
                className="w-full max-w-md rounded-2xl shadow-2xl"
              />
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Mukesh Rathore™
                </p>
                
                <div className="space-y-1">
                  <p className="text-base md:text-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center justify-center gap-2">
                    <span className="text-purple-600">🎙️</span>
                    Original Artist Builder for Singers
                  </p>
                  <p className="text-base md:text-lg font-semibold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent flex items-center justify-center gap-2">
                    <span className="text-orange-600">🏆</span>
                    India's #1 Indie Music Coach
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <motion.a
              href={WEBINAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                x: [0, -8, 8, -8, 8, 0]
              }}
              transition={{
                repeat: Infinity,
                duration: 0.8,
                ease: "easeInOut"
              }}
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 md:py-6 md:px-12 rounded-lg text-lg md:text-2xl transition-all shadow-2xl"
            >
              Join The Free Masterclass
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className={`py-10 md:py-20 px-2 md:px-6 pb-32 md:pb-40 bg-gradient-to-br ${currentTheme.colors.primary} text-white`}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              <span className="text-2xl md:text-5xl font-bold"> 🚀 Stop Waiting. Start Creating</span>
             
            </h2>
            <p className="text-2xl md:text-3xl mb-12 text-white/90">
              Your first original song is closer than you think
            </p>

            <motion.a
              href={WEBINAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                x: [0, -8, 8, -8, 8, 0]
              }}
              transition={{
                repeat: Infinity,
                duration: 0.8,
                ease: "easeInOut"
              }}
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 md:py-6 md:px-12 rounded-lg text-lg md:text-2xl transition-all shadow-2xl mb-8"
            >
              Join The Free Masterclass
            </motion.a>

            <p className="text-xl text-white/90">
              Free live session • Limited seats • No credit card required
            </p>
          </motion.div>
        </div>
      </section>
      {/* Countdown Timer always visible at bottom */}
      <CountdownTimer />
    </div>
  );
}

export default Webinar;
